import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { cn } from '../../lib/utils';
import { Mic, MessageSquare, Camera, ArrowRight, User, Calendar, FileText, Search } from 'lucide-react';
import { useAppMode } from '../../contexts/AppModeContext';

const DEMO_PATIENTS = [
  { id: 'P001', name: 'John Smith', dob: '1965-03-15', mrn: 'MRN-001', lastVisit: '2026-01-15' },
  { id: 'P002', name: 'Maria Garcia', dob: '1978-07-22', mrn: 'MRN-002', lastVisit: '2026-01-20' },
  { id: 'P003', name: 'Robert Johnson', dob: '1955-11-08', mrn: 'MRN-003', lastVisit: '2026-02-01' },
];

const LuminaNewEncounter = () => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [visitType, setVisitType] = useState('');
  const [chiefComplaint, setChiefComplaint] = useState('');
  const [captureMethod, setCaptureMethod] = useState(null);

  const filteredPatients = DEMO_PATIENTS.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.mrn.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleStartCapture = () => {
    if (captureMethod === 'ambient') {
      navigate('/lumina/ambient', { state: { patient: selectedPatient, visitType, chiefComplaint } });
    } else if (captureMethod === 'dictation') {
      navigate('/lumina/dictation', { state: { patient: selectedPatient, visitType, chiefComplaint } });
    } else if (captureMethod === 'lens') {
      navigate('/lumina/lens', { state: { patient: selectedPatient, visitType, chiefComplaint } });
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="new-encounter-page">
      {/* Workflow Progress */}
      <div className="flex items-center justify-center gap-4 py-4">
        <div className="flex items-center gap-2">
          <div className={cn(
            "h-8 w-8 rounded-full flex items-center justify-center font-semibold text-sm",
            step >= 1 ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-600"
          )}>1</div>
          <span className={cn("text-sm font-medium", step >= 1 ? "text-emerald-700" : "text-slate-500")}>Capture Visit</span>
        </div>
        <ArrowRight className="h-4 w-4 text-slate-400" />
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center font-semibold text-sm">2</div>
          <span className="text-sm text-slate-500">Generate SOAP Note</span>
        </div>
        <ArrowRight className="h-4 w-4 text-slate-400" />
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center font-semibold text-sm">3</div>
          <span className="text-sm text-slate-500">Review & Send to Billing</span>
        </div>
      </div>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">New Encounter</h1>
        <p className="text-slate-500 mt-1">Select patient and capture method to begin documentation</p>
      </div>

      {/* Patient Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">1. Select Patient</CardTitle>
          <CardDescription>Search for an existing patient or create new</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name or MRN..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            {filteredPatients.map((patient) => (
              <div
                key={patient.id}
                onClick={() => setSelectedPatient(patient)}
                className={cn(
                  "flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors border",
                  selectedPatient?.id === patient.id
                    ? "border-emerald-500 bg-emerald-50"
                    : "border-slate-200 hover:bg-slate-50"
                )}
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
                    <User className="h-5 w-5 text-slate-400" />
                  </div>
                  <div>
                    <p className="font-medium">{patient.name}</p>
                    <p className="text-sm text-slate-500">MRN: {patient.mrn} • DOB: {patient.dob}</p>
                  </div>
                </div>
                {selectedPatient?.id === patient.id && (
                  <Badge className="bg-emerald-100 text-emerald-700">Selected</Badge>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Visit Details */}
      {selectedPatient && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">2. Visit Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Visit Type</Label>
                <Select value={visitType} onValueChange={setVisitType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new_patient">New Patient</SelectItem>
                    <SelectItem value="follow_up">Follow-up</SelectItem>
                    <SelectItem value="annual">Annual Physical</SelectItem>
                    <SelectItem value="sick">Sick Visit</SelectItem>
                    <SelectItem value="procedure">Procedure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date of Service</Label>
                <Input type="date" defaultValue={new Date().toISOString().split('T')[0]} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Chief Complaint</Label>
              <Textarea
                placeholder="Enter chief complaint..."
                value={chiefComplaint}
                onChange={(e) => setChiefComplaint(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Capture Method */}
      {selectedPatient && visitType && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">3. Choose Capture Method</CardTitle>
            <CardDescription>Select how you want to document this visit</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div
                onClick={() => setCaptureMethod('ambient')}
                className={cn(
                  "p-6 rounded-xl border-2 cursor-pointer transition-all text-center",
                  captureMethod === 'ambient'
                    ? "border-emerald-500 bg-emerald-50"
                    : "border-slate-200 hover:border-slate-300"
                )}
              >
                <Mic className="h-10 w-10 text-emerald-600 mx-auto mb-3" />
                <p className="font-semibold">Ambient Listening</p>
                <p className="text-sm text-slate-500 mt-1">Passive background recording</p>
              </div>
              <div
                onClick={() => setCaptureMethod('dictation')}
                className={cn(
                  "p-6 rounded-xl border-2 cursor-pointer transition-all text-center",
                  captureMethod === 'dictation'
                    ? "border-blue-500 bg-blue-50"
                    : "border-slate-200 hover:border-slate-300"
                )}
              >
                <MessageSquare className="h-10 w-10 text-blue-600 mx-auto mb-3" />
                <p className="font-semibold">Dictation Mode</p>
                <p className="text-sm text-slate-500 mt-1">Push-to-record dictation</p>
              </div>
              <div
                onClick={() => setCaptureMethod('lens')}
                className={cn(
                  "p-6 rounded-xl border-2 cursor-pointer transition-all text-center",
                  captureMethod === 'lens'
                    ? "border-violet-500 bg-violet-50"
                    : "border-slate-200 hover:border-slate-300"
                )}
              >
                <Camera className="h-10 w-10 text-violet-600 mx-auto mb-3" />
                <p className="font-semibold">Lumina Lens</p>
                <p className="text-sm text-slate-500 mt-1">Import from existing charts</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Start Button */}
      {captureMethod && (
        <div className="flex justify-end">
          <Button
            onClick={handleStartCapture}
            className="bg-emerald-600 hover:bg-emerald-700 gap-2"
            size="lg"
          >
            Start Capture <ArrowRight className="h-5 w-5" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default LuminaNewEncounter;
